import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Label } from '@tyndo/ui-kit';
import { AuthShell, AuthError, AuthNotice } from './AuthShell';
import { resetPassword, AuthApiError } from '../auth/nativeAuth';
import { recordError } from '../observability/telemetry';

/**
 * Reset-password landing leg ('/auth/reset?token=…'). Reads the one-shot reset
 * token from the query string and POSTs it with the new password to
 * /api/v1/auth/reset-password.
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get('token');
    setToken(t);
    if (!t) {
      setError('This reset link is missing its token. Please use the link from your email.');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError('This reset link is missing its token. Please use the link from your email.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await resetPassword({ token, newPassword: password });
      setDone(true);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Could not reset your password.';
      setError(message);
      recordError('auth.reset_password_failed', err, {
        statusCode: err instanceof AuthApiError ? err.statusCode : undefined,
      });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <AuthShell title="Password reset">
        <div className="space-y-4">
          <AuthNotice>
            Your password has been reset. You can now sign in with your new
            password.
          </AuthNotice>
          <Button onClick={() => navigate('/login')} className="w-full">
            Go to sign in
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Choose a new password">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <AuthError message={error} />}

        <div className="space-y-1.5">
          <Label htmlFor="reset-password">New password</Label>
          <Input
            id="reset-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            disabled={loading || !token}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="reset-confirm">Confirm new password</Label>
          <Input
            id="reset-confirm"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Re-enter your new password"
            disabled={loading || !token}
          />
        </div>

        <Button type="submit" size="lg" disabled={loading || !token} className="w-full">
          {loading ? 'Resetting…' : 'Reset password'}
        </Button>
      </form>

      <p className="text-sm text-muted-foreground text-center mt-4">
        <Button
          variant="link"
          type="button"
          onClick={() => navigate('/login')}
          className="h-auto p-0 text-foreground font-medium underline"
        >
          Back to sign in
        </Button>
      </p>
    </AuthShell>
  );
}
